"use client";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import JobSearchDetail from "./JobSearchDetail";
import Logo from "../atoms/Logo";
import NavLinks from "../molecules/NavLinks";
import Button from "../atoms/Button";
import { Menu, X, Search, ChevronDown, Plus, Minus, Ban } from "lucide-react";

export default function HeaderDetail() {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = location.pathname.startsWith("/id") ? "id" : "en";
  const langPrefix = currentLang === "id" ? "/id" : "/en";

  const switchLanguage = (lang: "en" | "id") => {
    if (lang === currentLang) return; // jangan refresh kalau bahasa sama
    const newPath = location.pathname.replace(/^\/(id|en)/, `/${lang}`);
    navigate(newPath);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen || showSearch ? "hidden" : "";
  }, [isOpen, showSearch]);

  const navLabels = {
  id: {
    jobs: "Loker",
    blog: "Blog",
    news: "Berita",
    events: "Acara",
    tips: "Tips Karier",
    service: "Layanan Kami",
    headhunter: "Head Hunter",
    recruitment: "Rekrutmen Talent",
    hr: "Kepatuhan SDM",
  },
  en: {
    jobs: "Jobs",
    blog: "Blog",
    news: "News",
    events: "Events",
    tips: "Talent Tips",
    service: "Our Service",
    headhunter: "Head Hunter",
    recruitment: "Talent Recruitment",
    hr: "HR Compliance",
  },
};

// Gunakan di navItems
const navItems = [
  { label: navLabels[currentLang].jobs, to: `/jobs` },
  {
    label: navLabels[currentLang].blog,
    to: `/blog/news`,
    subItems: [
      { label: navLabels[currentLang].news, to: `${langPrefix}/blog/news` },
      { label: navLabels[currentLang].events, to: `${langPrefix}/blog/events` },
      // { label: navLabels[currentLang].tips, to: `${langPrefix}/blog/talent-tips` },
    ],
  },
  { label: navLabels[currentLang].service, to: `/our-service` },
];
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Searching:", searchValue);
    setShowSearch(false);
  };

  const toggleSubMenu = (label: string) => {
    setOpenSubMenu(openSubMenu === label ? null : label);
  };

  return (
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md" : "bg-white shadow-md"
      }`}
    >
      <div
        className={`container-banner py-4 flex justify-between items-center sm:px-16 lg:px-20 xl:px-28 max-w-[1920px] mx-auto transition-all duration-300 ${
          scrolled ? "sm:pt-4 lg:pt-8 xl:pt-4" : "sm:pt-4 lg:pt-8 xl:pt-4"
        }`}
      >
        <Link to={`/${currentLang}/homepage`} className="flex items-center">
          <Logo color={scrolled ? "black" : "black"} />
        </Link>


        {/* === Desktop Navigation === */}
        <div className="hidden lg:flex items-center gap-10 flex-1 relative">
          <nav className="flex gap-6 lg:ml-16 xl:ml-28 relative">
            {navItems.map((item) => {
               const fullPath = `/${currentLang}${item.to}`;
                const isActive = location.pathname.startsWith(fullPath);
              const textColor = scrolled
                ? isActive
                  ? "text-primary"
                  : "text-black"
                : isActive
                ? "text-primary"
                : "text-black";
              const hoverColor = scrolled
                ? "hover:text-primary"
                : "hover:text-primary";

              return (
                <div key={item.to} className="relative group">
                  <Link
                    to={fullPath}
                    className={`font-semibold flex items-center gap-1 transition-colors ${textColor} ${hoverColor}`}
                  >
                    {item.label}
                    {item.subItems && (
                      <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                    )}
                  </Link>

                  {item.subItems && (
                    <div className="absolute left-0 top-full mt-2 bg-white text-black rounded-xl shadow-lg opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 w-48 py-2 z-50">
                      {item.subItems.map((sub) => (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          className="block px-4 py-2 hover:bg-primary hover:text-white text-sm"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Buttons & Language */}
          <div className="ml-auto flex items-center gap-4 relative">
            <Button
              href={`/${currentLang}/login`}
              bgClass="bg-[#0274fb]"
              hoverBgClass="hover:bg-secondary"
              textClass="text-font-primary"
              hoverTextClass="hover:text-font-secondary"
              rounded="rounded-3xl"
              className="px-[3rem] py-[0.25rem]"
            >
              Login / Register
            </Button>

            <div className="flex gap-2 text-sm font-medium">
              <button
                onClick={() => switchLanguage("id")}
                className={`font-semibold transition-colors ${currentLang === "id"
                    ? scrolled
                      ? "text-primary"
                      : "text-primary"
                    : scrolled
                      ? "text-black hover:text-primary"
                      : "text-black hover:text-secondary"
                  }`}
              >
                ID
              </button>

              <div className={scrolled ? "text-black" : "text-black"}>|</div>

              <button
                onClick={() => switchLanguage("en")}
                className={`font-semibold transition-colors ${currentLang === "en"
                    ? scrolled
                      ? "text-primary"
                      : "text-primary"
                    : scrolled
                      ? "text-black hover:text-primary"
                      : "text-black hover:text-secondary"
                  }`}
              >
                EN
              </button>
            </div>



            {/* <button
              className={`ml-2 p-2 rounded-full transition-colors ${
                scrolled ? "text-black hover:text-secondary" : "text-white hover:text-secondary"
              }`}
              onClick={() => setShowSearch(true)}
            >
              <Search className="w-5 h-5" />
            </button> */}
          </div>
        </div>

        {/* === Mobile Menu Button === */}
        <button className="lg:hidden" onClick={() => setIsOpen(true)}>
          <Menu className={`w-6 h-6 ${scrolled ? "text-black" : "text-black"}`} />
        </button>
      </div>

      {/* === Mobile Sidebar === */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setIsOpen(false)}
      />

      <div
        className={`fixed top-0 left-0 h-full w-3/4 bg-white shadow-lg z-50 transform transition-transform duration-300 lg:hidden ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="py-4 px-6 flex flex-col gap-4 h-full overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <Logo color="black" />
            <button onClick={() => setIsOpen(false)}>
              <X className="w-6 h-6 text-black" />
            </button>
          </div>

          <nav className="flex flex-col gap-2">
            {navItems.map((item) => (
              <div key={item.to}>
                <div
                  className="flex justify-between items-center py-2 cursor-pointer"
                  onClick={() =>
                    item.subItems ? toggleSubMenu(item.label) : setIsOpen(false)
                  }
                >
                  <Link
                    to={`/${currentLang}${item.to}`}
                    className="font-semibold text-black hover:text-secondary"
                  >
                    {item.label}
                  </Link>
                  {item.subItems && (
                    <span>
                      {openSubMenu === item.label ? (
                        <Minus className="w-4 h-4 text-black" />
                      ) : (
                        <Plus className="w-4 h-4 text-black" />
                      )}
                    </span>
                  )}
                </div>

                {item.subItems && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ${openSubMenu === item.label ? "max-h-40" : "max-h-0"
                      }`}
                  >
                    {item.subItems.map((sub) => (
                      <Link
                        key={sub.to}
                        to={`/${currentLang}${sub.to}`}
                        onClick={() => setIsOpen(false)}
                        className="block pl-6 py-2 text-sm text-gray-700 hover:text-secondary"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="flex gap-2 text-sm font-medium">
              <button
                onClick={() => switchLanguage("id")}
                className={`font-semibold transition-colors ${currentLang === "id"
                    ? scrolled
                      ? "text-primary"
                      : "text-primary"
                    : scrolled
                      ? "text-black hover:text-primary"
                      : "text-black hover:text-secondary"
                  }`}
              >
                ID
              </button>

              <div className={scrolled ? "text-black" : "text-black"}>|</div>

              <button
                onClick={() => switchLanguage("en")}
                className={`font-semibold transition-colors ${currentLang === "en"
                    ? scrolled
                      ? "text-primary"
                      : "text-primary"
                    : scrolled
                      ? "text-black hover:text-primary"
                      : "text-black hover:text-secondary"
                  }`}
              >
                EN
              </button>
            </div>
           <hr className="my-4 border-gray-300" />

          <div className="flex flex-col gap-4">
            <Button
              href={`/${currentLang}/login`}
              bgClass="bg-[#0274fb]"
              hoverBgClass="hover:bg-secondary"
              textClass="text-font-primary"
              hoverTextClass="hover:text-font-secondary"
              rounded="rounded-3xl"
              className="px-[3rem] py-[0.25rem]"
            >
              Login / Register
            </Button>

            {/* <button
              className="flex items-center gap-2 text-black hover:text-secondary transition-colors"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Search className="w-5 h-5" />
              <span>Search</span>
            </button> */}

            {/* <div
              className={`overflow-hidden transition-all duration-300 ${
                showSearch
                  ? "max-h-40 opacity-100 mt-2"
                  : "max-h-0 opacity-0"
              }`}
            >
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div> */}
          </div>
        </div>
      </div>

      {/* === Fullscreen Search === */}
      {showSearch && (
        <div className="fixed hidden md:flex inset-0 bg-white/50 backdrop-blur-sm z-[60]  flex-col items-center justify-center transition-all duration-300">
          {/* <button
            onClick={() => setShowSearch(false)}
            className="absolute top-6 right-6 text-black hover:text-secondary transition-colors"
          >
            <X className="w-7 h-7" />
          </button> */}

          {/* <form
            onSubmit={handleSearchSubmit}
            className="w-full max-w-xl flex items-center border border-gray-300 rounded-full shadow-md overflow-hidden"
          >
            <input
              type="text"
              autoFocus
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Type to search..."
              className="flex-1 px-6 py-3 bg-white text-lg focus:outline-none bg-transparent"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-primary text-white rounded-r-full hover:bg-secondary transition-colors flex items-center gap-2"
            >
              <Search className="w-5 h-5" /> Search
            </button>
          </form> */}
        </div>
      )}
    </header>
  );
}
